import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Lock, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (query) {
      performSearch();
    }
  }, [query]);

  const performSearch = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/wiki/search?q=${encodeURIComponent(query)}`,
        { withCredentials: true }
      );
      setResults(data.results || []);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const highlightMatch = (text, query) => {
    if (!query) return text;
    const stripped = text.replace(/<[^>]*>/g, '').substring(0, 200);
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return stripped.replace(regex, '<mark class="bg-primary/30 text-foreground rounded px-0.5">$1</mark>');
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center" data-testid="search-loading">
        <div className="text-primary text-xl font-serif">Searching...</div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-80px)] py-12 px-6" data-testid="search-results-page">
      <div className="container mx-auto max-w-4xl">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Search className="w-8 h-8 text-primary" aria-hidden="true" />
            <h1 className="text-3xl lg:text-4xl font-serif tracking-tighter text-primary" data-testid="search-results-heading">
              Search Results
            </h1>
          </div>
          <p className="text-base font-sans text-muted-foreground">
            {results.length} {results.length === 1 ? 'entry' : 'entries'} found for "<span className="text-foreground">{query}</span>"
          </p>
        </div>

        {results.length === 0 ? (
          <div className="text-center py-16" data-testid="no-results-message">
            <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" aria-hidden="true" />
            <p className="text-lg font-sans text-muted-foreground mb-2">No entries found matching "{query}"</p>
            <p className="text-sm font-sans text-muted-foreground">Try searching for a different term or browse the <Link to="/wiki" className="text-primary hover:underline">full glossary</Link>.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {results.map((entry) => (
              <Link
                key={entry.id}
                to={`/wiki/${entry.id}`}
                className="block"
                data-testid={`search-result-${entry.id}`}
              >
                <Card className="bg-card border-border rounded-sm hover:border-primary/50 hover:shadow-[0_0_15px_rgba(245,158,11,0.15)] transition-all duration-300">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-xl font-serif text-foreground">
                        {entry.title}
                      </CardTitle>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge variant="secondary" className="rounded-sm font-sans text-xs">
                          {entry.category}
                        </Badge>
                        {entry.is_members_only && (
                          <Lock className="w-4 h-4 text-primary" aria-hidden="true" />
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription
                      className="font-sans text-foreground/80"
                      dangerouslySetInnerHTML={{ __html: highlightMatch(entry.content, query) + '...' }}
                    />
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-xs font-sans text-muted-foreground">
                        By {entry.author_name} &bull; {new Date(entry.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResultsPage;
